import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { UX2 } from '@wsb/guac-widget-core';
import dataAids from '../constants/dataAids';
import AddonCardUI from '../ui/AddonCardUI';
import { fetchAddonsData, selectAddon } from '../actions/AvailableTimeSelection';
import AvailableTimeSelectionStore from '../stores/AvailableTimeSelection';
import Fetching from './Fetching';

const AddonMenuItemHeight = 175;

const styles = {
  wrapper: {
    marginBottom: 'medium',
    textAlign: 'center'
  },
  grid: {
    width: '100%',
    marginTop: 'large',
    marginLeft: '-xsmall',
    marginRight: '-xsmall',
    display: 'flex',
    flexDirection: 'column'
  },
  resourceGroup: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    paddingRight: 0,
    paddingBottom: 0,
    paddingLeft: 0,
    transition: 'max-height 2s ease'
  },
  resource: {
    width: '100%',
    paddingRight: 'xsmall',
    marginBottom: 'small',
    paddingLeft: 'xsmall'
  },
  menuHeader: {
    width: '60%'
  }
};

const getMenuStyles = (isAddonMenuVisible, addOnData) => ({
  transition: 'max-height 0.5s',
  overflow: 'hidden',
  '@xs-only': {
    maxHeight: isAddonMenuVisible
      ? addOnData?.length * AddonMenuItemHeight + AddonMenuItemHeight + 'px'
      : '0px'
  },
  '@sm': {
    maxHeight: isAddonMenuVisible
      ? addOnData?.length * AddonMenuItemHeight + AddonMenuItemHeight + 'px'
      : '0px'
  },
  '@md': {
    paddingHorizontal: 'large'
  },
  '@lg': {
    paddingHorizontal: 'xlarge'
  }
});

const AddonMenu = ({ service, defaultAddonData = [] }) => {
  const [addonData, setAddonData] = useState(defaultAddonData);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [isAddonMenuVisible, setIsAddonMenuVisible] = useState(true);
  const { selectedAddons } = AvailableTimeSelectionStore.getState();

  useEffect(() => {
    setIsLoading(true);

    fetchAddonsData({ serviceId: service.id })
      .then(res => {
        const addonData = res.map(data => data.addon);
        setAddonData(addonData);
        selectAddon([]);
      })
      .catch(() => {
        setIsError(true);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [service.id]);

  const toggleAddonMenu = () => {
    setIsAddonMenuVisible(prevValue => !prevValue);
  };

  const handleAddonClick = id => {
    let newselectedAddons = [];
    const isAlreadySelected = selectedAddons?.filter(selected => selected.id === id).length > 0;

    if (isAlreadySelected) {
      newselectedAddons = selectedAddons.filter(selected => selected.id !== id);
    } else {
      const newAddonData = addonData.find(addOn => addOn.id === id);
      newselectedAddons = [...selectedAddons, newAddonData];
    }

    selectAddon(newselectedAddons);
  };

  const menuStyles = getMenuStyles(isAddonMenuVisible, addonData);

  const getMenuHeader = () => {
    const defaultHeader = 'Add-ons available for choosen service';

    if (isAddonMenuVisible) return defaultHeader;

    const menuHeader = selectedAddons?.map(addOn => addOn.title)?.join(', ');

    return menuHeader ? menuHeader : defaultHeader;
  };

  const { Block, MoreLink } = UX2.Element;

  if (isError) return null;

  if (isLoading) return <Fetching fetching />;

  if (addonData.length === 0) return null;

  return (
    <Block style={ styles.wrapper }>
      <MoreLink.Menu
        onClick={ toggleAddonMenu }
        data-aid={ dataAids.ADDON_MENU_OPENER }
        expanded={ isAddonMenuVisible }
        tag='span'
        style={ styles.menuHeader }
      >
        { getMenuHeader() }
      </MoreLink.Menu>
      <Block style={ menuStyles }>
        <Block style={ styles.grid }>
          { addonData?.map(addOn => {
            const isSelected =
              selectedAddons?.filter(selected => selected.id === addOn.id).length > 0;
            return (
              <Block key={ addOn.id } style={ styles.resourceGroup }>
                <Block style={ styles.resource }>
                  <AddonCardUI
                    addOn={ addOn }
                    isSelected={ isSelected }
                    handleAddonClick={ handleAddonClick }
                  />
                </Block>
              </Block>
            );
          }) }
        </Block>
      </Block>
    </Block>
  );
};

AddonMenu.propTypes = {
  service: PropTypes.object.isRequired,
  defaultAddonData: PropTypes.array
};

export default AddonMenu;
